/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Navbar from '../Navbar';
import Header from '../Header';
import Loader from '../Loader';
import baseUrl from '../BaseUrl';
import ConfirmDialog from './ConfirmDialog';
import './delete-user-data.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAILS = 100;

const CONFIRM_1 = 'Are you sure you want to delete the selected user(s)?';
const CONFIRM_2 = 'This action is irreversible. Do you really want to permanently delete all user data?';

/**
 * Parse raw textarea input into a deduped lowercase trimmed list of emails
 * with per-entry malformed reasons.
 */
function parseEmailsLocal(raw) {
  if (typeof raw !== 'string') return { emails: [], malformed: [] };
  const tokens = raw
    .split(/[\s,;]+/)
    .map(t => t.trim())
    .filter(Boolean);
  const seen = new Set();
  const emails = [];
  const malformed = [];
  for (const t of tokens) {
    const e = t.toLowerCase();
    if (!EMAIL_REGEX.test(e)) {
      malformed.push({ email: t, reason: 'Invalid email format' });
      continue;
    }
    if (!seen.has(e)) {
      seen.add(e);
      emails.push(e);
    }
  }
  return { emails, malformed };
}

const DeleteUserData = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [, setExpire] = useState('');
  const [text, setText] = useState('');
  const [clientErrors, setClientErrors] = useState([]);
  const [serverErrors, setServerErrors] = useState([]);
  const [serverMessage, setServerMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('idle'); // idle | confirm1 | confirm2
  const [pendingEmails, setPendingEmails] = useState([]);
  const [summary, setSummary] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState('');
  const [displayToDate, setDisplayToDate] = useState('');

  useEffect(() => {
    refreshToken();
  }, []);

  useEffect(() => {
    setName('Pauser');
  }, []);

  // Auto-dismiss success / error banners after 5s.
  useEffect(() => {
    if (!successMessage) return undefined;
    const t = setTimeout(() => setSuccessMessage(''), 5000);
    return () => clearTimeout(t);
  }, [successMessage]);

  useEffect(() => {
    if (!serverMessage) return undefined;
    const t = setTimeout(() => {
      setServerMessage('');
      setServerErrors([]);
    }, 5000);
    return () => clearTimeout(t);
  }, [serverMessage]);

  useEffect(() => {
    if (clientErrors.length === 0) return undefined;
    const t = setTimeout(() => setClientErrors([]), 5000);
    return () => clearTimeout(t);
  }, [clientErrors]);

  const refreshToken = async () => {
    try {
      const response = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(response.data.accessToken);
      const decoded = jwtDecode(response.data.accessToken);
      setExpire(decoded.exp);
    } catch (error) {
      if (error.response) navigate('/');
    }
  };

  const loadSummary = async (from, to) => {
    try {
      const url = `${baseUrl}/admin/api/user-deletion-summary${from && to ? `?from_date=${from}&to_date=${to}` : ''}`;
      const res = await axios.get(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res?.data?.success) setSummary(res.data.data);
    } catch (err) {
      // Summary failure must not block deletion form
      console.log(err);
    }
  };

  useEffect(() => {
    if (token) loadSummary('', '');
  }, [token]);

  const handleFromDate = (date) => {
    const fmt = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    setFromDate(fmt); setDisplayFromDate(date);
  };
  const handleToDate = (date) => {
    const fmt = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    setToDate(fmt); setDisplayToDate(date);
  };
  const handleApply = () => { if (fromDate && toDate) loadSummary(fromDate, toDate); };
  const handleClear = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(''); setDisplayToDate('');
    loadSummary('', '');
  };
  const fmtDelta = (n) => (n === null || n === undefined) ? '—' : `${n > 0 ? '+' : ''}${n}%`;
  const deltaClass = (n) => (n === null || n === undefined) ? '' : (n > 0 ? 'delta-up' : (n < 0 ? 'delta-down' : ''));
  const summaryAll = summary?.all_time || {};
  const summaryPeriod = summary?.period;

  const resetMessages = () => {
    setClientErrors([]);
    setServerErrors([]);
    setServerMessage('');
    setSuccessMessage('');
  };

  const handleDeleteClick = () => {
    resetMessages();
    const trimmed = text.trim();
    if (!trimmed) {
      setClientErrors([{ email: '', reason: 'Enter at least one email address' }]);
      return;
    }
    const { emails, malformed } = parseEmailsLocal(trimmed);
    if (malformed.length > 0) {
      setClientErrors(malformed);
      return;
    }
    if (emails.length === 0) {
      setClientErrors([{ email: '', reason: 'Enter at least one email address' }]);
      return;
    }
    if (emails.length > MAX_EMAILS) {
      setClientErrors([{ email: '', reason: `Maximum ${MAX_EMAILS} emails per request` }]);
      return;
    }
    setPendingEmails(emails);
    setStep('confirm1');
  };

  const handleConfirm1 = () => setStep('confirm2');
  const handleCancel1 = () => { setStep('idle'); setPendingEmails([]); };
  const handleConfirm2 = () => {
    setStep('idle');
    runDeletion(pendingEmails);
  };
  const handleCancel2 = () => { setStep('idle'); setPendingEmails([]); };

  const runDeletion = async (emails) => {
    setLoading(true);
    resetMessages();
    try {
      const res = await axios.post(
        `${baseUrl}/admin/api/hard-delete-user`,
        { emails },
        {
          timeout: 30000,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      const count = res?.data?.data?.deleted_user_count ?? emails.length;
      setSuccessMessage(`${count} user(s) deleted`);
      setText('');
      setPendingEmails([]);
      loadSummary(fromDate, toDate);
    } catch (error) {
      const apiBody = error?.response?.data;
      if (apiBody?.message) {
        setServerMessage(apiBody.message);
        if (Array.isArray(apiBody.errors)) setServerErrors(apiBody.errors);
      } else if (error?.code === 'ECONNABORTED') {
        setServerMessage('Request timed out. The operation may have completed; please refresh and re-check.');
      } else {
        setServerMessage('Internal Server Error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name={name} />
        {loading && <Loader />}
        <div className="content-page">
          <div className="row">
            <div className="col-6">
              <div className="left-title">
                <h1>
                  Delete User Data
                  <span className="user-deletion-tagline">
                    Permanently delete all data for one or more users. This action cannot be undone.
                  </span>
                </h1>
              </div>
            </div>
            <div className="col-6">
              <div className="addnew-button">
                <Link to="/dashboard"><span>&larr;</span> Back</Link>
              </div>
            </div>
          </div>
          <div className="row numberboxes">
            <div className="col-md-3">
              <div className="dashboard-box" id="number">
                <h3>Total Requests
                  <span className="kpi-hint">All hard-delete attempts ever logged (success + validation_failed + rolled_back)</span>
                </h3>
                <p>{summaryAll.total_requests ?? '—'}</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="dashboard-box" id="number">
                <h3>Successful
                  <span className="kpi-hint">Requests where every target user was deleted across all 23 child tables</span>
                </h3>
                <p>{summaryAll.success_requests ?? '—'}</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="dashboard-box" id="number">
                <h3>Failed / Rolled-back
                  <span className="kpi-hint">Validation errors (bad emails, conflicts) plus mid-deletion rollbacks</span>
                </h3>
                <p>{(summaryAll.validation_failed ?? 0) + (summaryAll.rolled_back ?? 0)}</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="dashboard-box" id="number">
                <h3>Users Deleted (total)
                  <span className="kpi-hint">Distinct user accounts removed across every successful request</span>
                </h3>
                <p>{summaryAll.total_users_deleted ?? '—'}</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="dashboard-box" id="number">
                <h3>Today / 7d / 30d
                  <span className="kpi-hint">Deletion requests in the last 1 / 7 / 30 days (rolling, by audit created_at)</span>
                </h3>
                <p>{summaryAll.requests_today ?? '—'} / {summaryAll.requests_7d ?? '—'} / {summaryAll.requests_30d ?? '—'}</p>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 order-filter transection-filter">
              <div className="boxinput">
                <DatePicker selected={displayFromDate} onChange={handleFromDate} dateFormat="MM/dd/yyyy" placeholderText="From Date" className="from-date" />
                <DatePicker selected={displayToDate} onChange={handleToDate} dateFormat="MM/dd/yyyy" placeholderText="To Date" className="end-date" />
                <button className="search-btn" onClick={handleApply} disabled={!fromDate || !toDate}>Apply</button>
                {(fromDate || toDate) && (<button type="button" className="search-btn clear-btn" onClick={handleClear}>Clear</button>)}
              </div>
            </div>
          </div>

          {summaryPeriod && (
            <div className="row numberboxes" data-testid="user-deletion-period-boxes">
              <div className="col-12">
                <div className="left-title">
                  <h3>Selected period: {summaryPeriod.from_date} → {summaryPeriod.to_date}
                    <span className="period-sub"> (vs {summaryPeriod.prior_from_date} → {summaryPeriod.prior_to_date})</span>
                  </h3>
                </div>
              </div>
              <div className="col-md-3">
                <div className="dashboard-box" id="number">
                  <h3>Requests
                    <span className="kpi-hint">Deletion attempts logged in the selected range; delta vs same-length prior window</span>
                  </h3>
                  <p>{summaryPeriod.requests} <span className={`delta ${deltaClass(summaryPeriod.delta_requests_pct)}`}>{fmtDelta(summaryPeriod.delta_requests_pct)}</span></p>
                </div>
              </div>
              <div className="col-md-3">
                <div className="dashboard-box" id="number">
                  <h3>Successful
                    <span className="kpi-hint">Successful deletion requests in the selected range; delta vs prior window</span>
                  </h3>
                  <p>{summaryPeriod.success_requests} <span className={`delta ${deltaClass(summaryPeriod.delta_success_pct)}`}>{fmtDelta(summaryPeriod.delta_success_pct)}</span></p>
                </div>
              </div>
              <div className="col-md-3">
                <div className="dashboard-box" id="number">
                  <h3>Users Deleted
                    <span className="kpi-hint">Distinct user accounts removed in the selected range; delta vs prior window</span>
                  </h3>
                  <p>{summaryPeriod.users_deleted} <span className={`delta ${deltaClass(summaryPeriod.delta_users_deleted_pct)}`}>{fmtDelta(summaryPeriod.delta_users_deleted_pct)}</span></p>
                </div>
              </div>
              <div className="col-md-3">
                <div className="dashboard-box" id="number">
                  <h3>Failed
                    <span className="kpi-hint">Validation errors + mid-deletion rollbacks in the selected range</span>
                  </h3>
                  <p>{summaryPeriod.failed_requests}</p>
                </div>
              </div>
            </div>
          )}

          <div className="row">
            <div className="col-md-12">
              <div className="dashboard-box user-deletion-box" data-testid="user-deletion-card">
                {successMessage && (
                  <div className="user-deletion-success-banner" role="status">
                    {successMessage}
                  </div>
                )}

                {serverMessage && (
                  <div className="user-deletion-error-banner" role="alert">
                    {serverMessage}
                  </div>
                )}

                <div className="row">
                  <div className="col-md-12">
                    <label htmlFor="user-deletion-emails" className="user-deletion-label">
                      Email addresses (comma or newline separated)
                    </label>
                    <textarea
                      id="user-deletion-emails"
                      data-testid="user-deletion-emails"
                      className="form-control user-deletion-input"
                      rows={6}
                      value={text}
                      onChange={e => setText(e.target.value)}
                      placeholder={'alice@example.com, bob@example.com\ncharlie@example.com'}
                      disabled={loading}
                    />
                  </div>
                </div>

                {(clientErrors.length > 0 || serverErrors.length > 0) && (
                  <ul className="user-deletion-error-list" role="alert">
                    {clientErrors.map((er, i) => (
                      <li key={`client-${i}`}>
                        {er.email ? <strong>{er.email}</strong> : null}
                        {er.email ? ' — ' : null}
                        {er.reason}
                      </li>
                    ))}
                    {serverErrors.map((er, i) => (
                      <li key={`server-${i}`}>
                        {er.email ? <strong>{er.email}</strong> : null}
                        {er.email ? ' — ' : null}
                        {er.reason}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="user-deletion-actions">
                  <button
                    type="button"
                    className="user-deletion-danger-btn"
                    onClick={handleDeleteClick}
                    disabled={loading || !text.trim()}
                    data-testid="user-deletion-delete-btn"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={step === 'confirm1'}
        title="Confirm deletion"
        message={CONFIRM_1}
        confirmLabel="Yes, continue"
        cancelLabel="Cancel"
        onConfirm={handleConfirm1}
        onCancel={handleCancel1}
        danger={true}
      />
      <ConfirmDialog
        open={step === 'confirm2'}
        title="This is permanent"
        message={CONFIRM_2}
        confirmLabel="Permanently delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirm2}
        onCancel={handleCancel2}
        danger={true}
      />
    </div>
  );
};

export default DeleteUserData;
